import axios from "axios";

// Cache storage with TTL
const cache = new Map();
const DEFAULT_CACHE_TTL = 5 * 60 * 1000; // 5 minutes in milliseconds

// Generate a unique cache key for a request
const generateCacheKey = (config) => {
  const { url, params = {}, data = {} } = config;
  return `${config.method || "GET"}-${url}-${JSON.stringify(
    params,
  )}-${JSON.stringify(data)}`;
};

// Make the actual API request
const makeRequest = async (config) => {
  try {
    const response = await axios(config);
    const { data } = response;

    return {
      data,
      error: null,
      // Part of some endpoint contracts (e.g. /stop: 200 = synchronously
      // composed vs 202 = accepted, composing async) — success carries the
      // HTTP status just as the error envelope below does.
      status: response.status,
    };
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error;
      const { response } = axiosError;
      let message = "http request failed";

      if (response && response.statusText) {
        message = response.statusText;
      }

      if (axiosError.message) {
        message = axiosError.message;
      }

      if (response && response.data && response.data.message) {
        message = response.data.message;
      }

      return {
        data: null,
        error: {
          message,
          status: response?.status,
          // Retry-After is CORS-exposed by the API for rate-limited share
          // requests; propagate it so callers can honor it (headers are not
          // otherwise surfaced by this envelope).
          retryAfter: Number(response?.headers?.["retry-after"]) || undefined,
        },
      };
    }

    return {
      data: null,
      error: {
        message: error.message,
      },
    };
  }
};

// Bumped by every clearCache(). A GET that was in flight across an
// invalidation was answered from before the change that caused it, so its
// result must not be written back into the cache.
let cacheEpoch = 0;

// Clear cache entries for a specific URL pattern
export const clearCache = (urlPattern) => {
  cacheEpoch += 1;
  console.log(`[API Cache] clearCache called with pattern: ${urlPattern}`);

  if (!urlPattern) {
    console.log(`[API Cache] Clearing entire cache`);
    cache.clear();
    return;
  }

  const keys = Array.from(cache.keys());
  console.log(`[API Cache] Current cache keys:`, keys);

  let clearedCount = 0;
  keys.forEach((key) => {
    if (key.includes(urlPattern)) {
      console.log(`[API Cache] Deleting cache key: ${key}`);
      cache.delete(key);
      clearedCount++;
    }
  });

  console.log(
    `[API Cache] Cleared ${clearedCount} entries matching pattern: ${urlPattern}`,
  );
};

export const callExternalApi = async (options) => {
  const { config, skipCache = false, cacheTTL = DEFAULT_CACHE_TTL } = options;

  // Only cache GET requests unless explicitly told to skip cache
  if ((!config.method || config.method === "GET") && !skipCache) {
    const cacheKey = generateCacheKey(config);

    // Check if we have a valid cached response
    if (cache.has(cacheKey)) {
      const cachedItem = cache.get(cacheKey);
      if (Date.now() < cachedItem.expiresAt) {
        console.log(`[Cache] Hit for ${config.url}`);
        return cachedItem.response;
      }
      console.log(`[Cache] Expired for ${config.url}`);
      cache.delete(cacheKey); // Remove expired entry
    }

    // Make the request and cache the result
    const epoch = cacheEpoch;
    const result = await makeRequest(config);

    if (!result.error && epoch === cacheEpoch) {
      console.log(`[Cache] Storing ${config.url}`);
      cache.set(cacheKey, {
        response: result,
        expiresAt: Date.now() + cacheTTL,
      });
    }

    return result;
  }

  // Non-GET requests or skipCache=true bypass cache
  return await makeRequest(config);
};
